import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SettingsProvider } from "@/context/SettingsContext";

import { StoreLayout } from "@/components/store/StoreLayout";
import { AdminRoute } from "@/components/store/RouteGuards";
import { AdminBadgeProvider } from "@/context/AdminBadgeContext";

import Home from "@/pages/store/Home";
import Catalog from "@/pages/store/Catalog";
import ProductDetail from "@/pages/store/ProductDetail";
import Collections from "@/pages/store/Collections";
import Cart from "@/pages/store/Cart";
import OrderConfirmation from "@/pages/store/OrderConfirmation";
import About from "@/pages/store/About";
import Contact from "@/pages/store/Contact";
import StoreReviews from "@/pages/store/StoreReviews";
import Accessories from "@/pages/store/Accessories";
import Blog from "@/pages/store/Blog";
import FAQ from "@/pages/store/FAQ";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminClients from "@/pages/admin/AdminClients";
import ProductManager from "@/pages/admin/ProductManager";
import AdminAccessories from "@/pages/admin/AdminAccessories";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminSettings from "@/pages/admin/AdminSettings";
import AuthCallback from "@/pages/admin/AuthCallback";

function AppRoutes() {
  const location = useLocation();
  // Handle the Google OAuth return (session_id in the URL fragment) BEFORE any route/guard runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<StoreLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Catalog />} />
        <Route path="/category/:category" element={<Catalog />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/collections/:name" element={<Catalog />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/order/:id" element={<OrderConfirmation />} />
        <Route path="/accessories" element={<Accessories />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/reviews" element={<StoreReviews />} />
      </Route>

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminBadgeProvider><AdminLayout /></AdminBadgeProvider></AdminRoute>}>
        <Route index element={<Navigate to="/admin/requests" replace />} />
        <Route path="requests" element={<AdminRequests />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="inventory/men" element={<ProductManager section="men" />} />
        <Route path="inventory/women" element={<ProductManager section="women" />} />
        <Route path="inventory/accessories" element={<AdminAccessories />} />
        <Route path="inventory/watches" element={<ProductManager section="watches" />} />
        <Route path="content/blog" element={<AdminBlog />} />
        <Route path="content/faqs" element={<AdminFaqs />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <SettingsProvider>
            <CartProvider>
              <BrowserRouter>
                <Toaster
                  position="top-center"
                  toastOptions={{
                    style: {
                      borderRadius: "2px",
                      fontFamily: "Outfit, sans-serif",
                    },
                  }}
                />
                <AppRoutes />
              </BrowserRouter>
            </CartProvider>
          </SettingsProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
